import { memo, useCallback } from 'react'

import { Divider, SvgIcon } from '@mui/material'
import { OrganizationRoleRamMember } from '@nativewaves/nw-cloud-sdk/org'
import { PlugDisconnected } from 'emotion-icons/fluentui-system-filled'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { MenuItemConfirm, MenuItemCopyId } from '@shared/components/MaterialUIEnhancements/Menu/MenuItem'
import { RowActionsProps } from '@shared/components/Table'
import { useOrganizationMemberDeleteMutation } from 'hooks/mutations/org'
import { membersRoute } from 'pages/NWPlatform/Organizations/Organization/Members'
import { resolveDisplayName } from 'utils/userInfo'

const RowActionsRaw = ({ row, onClose }: RowActionsProps<OrganizationRoleRamMember>) => {
  const { t } = useTranslation(['common', 'domain', 'entity'])
  const { enqueueSnackbar } = useSnackbar()

  const { id, memberType, teamInfo, userInfo } = row.original
  const derivedMemberInfo = memberType === 'User' ? userInfo! : teamInfo!

  const { organizationId } = membersRoute.useParams()

  const { mutate: deleteMember } = useOrganizationMemberDeleteMutation()

  const handleDeleteMember = useCallback(() => {
    onClose()

    deleteMember(
      { organizationId, memberId: id },
      {
        onSuccess: () =>
          enqueueSnackbar(t('domain:My.Organization.nameDelete', { name: resolveDisplayName(userInfo) }), {
            variant: 'success',
          }),
      },
    )
  }, [deleteMember, enqueueSnackbar, organizationId, onClose, id, userInfo, t])

  return (
    <>
      <MenuItemCopyId onSuccess={onClose} id={derivedMemberInfo.id ?? ''} />
      <Divider sx={{ my: 1 }} component="li" />
      <MenuItemConfirm
        onConfirm={handleDeleteMember}
        staticLabel={t('domain:Organization.removeMember')}
        startIcon={
          <SvgIcon color="error">
            <PlugDisconnected />
          </SvgIcon>
        }
        ConfirmTypographyProps={{ color: 'error' }}
      />
    </>
  )
}

export const RowActions = memo(RowActionsRaw)
